/**
 * PathwayModal — composable JSX-to-atom bridge + singleton renderer.
 *
 * Usage:
 *
 *   // 1. Render the singleton ONCE high in the tree (e.g. App.tsx):
 *   <PathwayModalRenderer />
 *
 *   // 2. In any component, compose the modal declaratively (returns null):
 *   <PathwayModal isOpen={open} onClose={close} stepKey={step}>
 *     <PathwayModal.Header>
 *       <PathwayModal.Title>My Title</PathwayModal.Title>
 *       <Searchbar />  {/* optional extra header content *\/}
 *     </PathwayModal.Header>
 *     <PathwayModal.Body><MyBody /></PathwayModal.Body>
 *     <PathwayModal.Footer>
 *       <PathwayModal.SecondaryButton onClick={onCancel}>Cancel</PathwayModal.SecondaryButton>
 *       <PathwayModal.PrimaryButton onClick={onSave} disabled={!valid}>Save</PathwayModal.PrimaryButton>
 *     </PathwayModal.Footer>
 *   </PathwayModal>
 */
import React, {
  MouseEventHandler,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { Button, ButtonVariant } from '@cfa/react-components';
import { Colors } from '@cfa/react-components/dist/cjs/internal/types';
import { Modal } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import ScrollModalFooter from '@/components/ScrollModal/ScrollModalFooterCore';
import ScrollModalHeader from '@/components/ScrollModal/ScrollModalHeaderCore';

import {
  pathwayModalAtom,
  pathwayModalInitialState,
} from '@/atoms/pathwayModal';

// ---------------------------------------------------------------------------
// Marker sub-components — all return null; they are pure data carriers.
// ---------------------------------------------------------------------------

export interface TitleProps {
  /** The modal header title string. */
  children: string;
}
const Title: React.FC<TitleProps> = () => null;
Title.displayName = 'PathwayModal.Title';

export interface HeaderProps {
  /** Title (required) plus optional extra content (e.g. a Searchbar). */
  children: React.ReactNode;
}
const Header: React.FC<HeaderProps> = () => null;
Header.displayName = 'PathwayModal.Header';

export interface BodyProps {
  children: React.ReactNode;
}
const Body: React.FC<BodyProps> = () => null;
Body.displayName = 'PathwayModal.Body';

export interface FooterProps {
  /**
   * Either PrimaryButton / SecondaryButton children for default layout,
   * or fully custom content that replaces the button pair entirely.
   */
  children: React.ReactNode;
}
const Footer: React.FC<FooterProps> = () => null;
Footer.displayName = 'PathwayModal.Footer';

export interface ModalButtonProps {
  children: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  loading?: boolean;
  color?: Colors;
  variant?: ButtonVariant;
  testId?: string;
}
const PrimaryButton: React.FC<ModalButtonProps> = () => null;
PrimaryButton.displayName = 'PathwayModal.PrimaryButton';

const SecondaryButton: React.FC<ModalButtonProps> = () => null;
SecondaryButton.displayName = 'PathwayModal.SecondaryButton';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Collect only valid React elements from a ReactNode (strips strings, nulls, etc.). */
const validElements = (children: React.ReactNode): React.ReactElement[] =>
  React.Children.toArray(children).filter(React.isValidElement);

/**
 * Recursively flatten fragments to get all non-fragment elements.
 * Handles cases where elements are nested inside fragments.
 */
const flattenFragments = (
  elements: React.ReactElement[],
): React.ReactElement[] => {
  const result: React.ReactElement[] = [];

  for (const el of elements) {
    if (el.type === React.Fragment) {
      // Recursively flatten the fragment's children
      const fragmentChildren = validElements(
        (el.props as { children?: React.ReactNode }).children,
      );
      result.push(...flattenFragments(fragmentChildren));
    } else {
      result.push(el);
    }
  }

  return result;
};

/**
 * Cast the `props` of a ReactElement to a known interface.
 * React 19 types `props` as `unknown`; passing the component as `_type` lets
 * TypeScript infer `P` from the component's props, avoiding explicit type
 * parameters at every call site while keeping `P` in two positions.
 */
// The trailing comma in <P,> disambiguates the generic from a JSX tag in .tsx files.
const propsOf = <P,>(el: React.ReactElement, _type: React.FC<P>): P =>
  el.props as P;

/**
 * Generate a unique signature from modal content.
 * Used as automatic stepKey if one isn't explicitly provided.
 */
const generateContentSignature = (
  headerTitle: string,
  bodyContent: React.ReactNode,
  primaryButtonText: string,
  footerContent: React.ReactNode,
): string =>
  [
    headerTitle,
    // For React elements, use a simplified representation
    bodyContent ? 'has-body' : 'no-body',
    primaryButtonText,
    footerContent ? 'has-footer' : 'no-footer',
  ]
    .filter(Boolean)
    .join('|');

// ---------------------------------------------------------------------------
// PathwayModal (composer) — returns null, syncs atom state from JSX children
// ---------------------------------------------------------------------------

export interface PathwayModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepKey?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
  showCloseButton?: boolean;
  children?: React.ReactNode;
}

/**
 * Composable modal that syncs JSX structure to a singleton atom.
 *
 * ⚠️ **Single modal per page:** Only one `<PathwayModal>` should be active at a time.
 * If multiple are rendered with `isOpen={true}`, the last one wins; animations won't work.
 * Use a single modal with conditional body content and discrete `stepKey` values instead.
 *
 * @example
 * // ✅ CORRECT: Single modal, conditional body
 * <PathwayModal isOpen={open} onClose={close} stepKey={step}>
 *   <PathwayModal.Header>
 *     <PathwayModal.Title>{step === 'a' ? 'Step A' : 'Step B'}</PathwayModal.Title>
 *   </PathwayModal.Header>
 *   <PathwayModal.Body>
 *     {step === 'a' ? <StepABody /> : <StepBBody />}
 *   </PathwayModal.Body>
 * </PathwayModal>
 *
 * @example
 * // ❌ WRONG: Multiple modals with competing stepKeys
 * <PathwayModal isOpen={open1} stepKey="modal1">...</PathwayModal>
 * <PathwayModal isOpen={open2} stepKey="modal2">...</PathwayModal>
 */
// Global tracker for active modals in development mode
// Exposed for testing purposes
// eslint-disable-next-line react-refresh/only-export-components
export const __DEV_activeModalsRef = { current: new Set<string>() };
const STEP_DURATION = 150; // Total duration of fade-out + fade-in

const PathwayModalComposer: React.FC<PathwayModalProps> = ({
  isOpen,
  onClose,
  stepKey,
  size,
  className,
  overlayClassName,
  overlayStyle,
  showCloseButton,
  children,
}) => {
  const topLevel = flattenFragments(validElements(children));

  // --- Dev-mode warning for multiple competing modals ---
  const modalIdRef = useRef(useId());

  useEffect(() => {
    const modalId = modalIdRef.current;

    if (process.env.NODE_ENV === 'development' && isOpen) {
      const prevSize = __DEV_activeModalsRef.current.size;
      __DEV_activeModalsRef.current.add(modalId);

      if (prevSize > 0) {
        console.warn(
          '[PathwayModal] Warning: Multiple active PathwayModal components detected. ' +
            'Only the last rendered modal will be active and animations may not work. ' +
            'Use a single <PathwayModal> with conditional body content instead. ' +
            `(${__DEV_activeModalsRef.current.size} modals currently active)`,
        );
      }
    } else if (process.env.NODE_ENV === 'development' && !isOpen) {
      __DEV_activeModalsRef.current.delete(modalId);
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        __DEV_activeModalsRef.current.delete(modalId);
      }
    };
  }, [isOpen]);

  // --- Header: extract Title + optional extra content ---
  const headerEl = topLevel.find(c => c.type === Header);
  let headerTitle = '';
  let headerContent: React.ReactNode = undefined;
  if (headerEl) {
    const hChildren = validElements(propsOf(headerEl, Header).children);
    const titleEl = hChildren.find(c => c.type === Title);
    headerTitle = titleEl ? propsOf(titleEl, Title).children : '';
    const extra = hChildren.filter(c => c.type !== Title);
    if (extra.length === 1) headerContent = extra[0];
    else if (extra.length > 1) headerContent = <>{extra}</>;
  }

  // --- Body ---
  const bodyEl = topLevel.find(c => c.type === Body);
  const bodyContent: React.ReactNode = bodyEl
    ? propsOf(bodyEl, Body).children
    : undefined;

  // --- Footer: PrimaryButton/SecondaryButton or custom content ---
  const footerEl = topLevel.find(c => c.type === Footer);
  let footerContent: React.ReactNode = undefined;
  let primaryButtonText = '';
  let primaryButtonHandler: MouseEventHandler<HTMLButtonElement> | undefined;
  let primaryButtonColor: Colors | undefined;
  let primaryButtonVariant: ButtonVariant | undefined;
  let primaryButtonTestId: string | undefined;
  let isDisabled: boolean | undefined;
  let isLoading: boolean | undefined;
  let secondaryButtonText: string | undefined;
  let secondaryButtonHandler: MouseEventHandler<HTMLButtonElement> | undefined;
  let secondaryButtonColor: Colors | undefined;
  let secondaryButtonTestId: string | undefined;

  if (footerEl) {
    const fChildren = validElements(propsOf(footerEl, Footer).children);
    const primaryBtn = fChildren.find(c => c.type === PrimaryButton);
    const secondaryBtn = fChildren.find(c => c.type === SecondaryButton);
    const custom = fChildren.filter(
      c => c.type !== PrimaryButton && c.type !== SecondaryButton,
    );

    if (custom.length > 0) {
      footerContent = custom.length === 1 ? custom[0] : <>{custom}</>;
    } else {
      if (primaryBtn) {
        const p = propsOf(primaryBtn, PrimaryButton);
        primaryButtonText = p.children;
        primaryButtonHandler = p.onClick;
        primaryButtonColor = p.color;
        primaryButtonVariant = p.variant;
        isDisabled = p.disabled;
        isLoading = p.loading;
        primaryButtonTestId = p.testId;
      }
      if (secondaryBtn) {
        const s = propsOf(secondaryBtn, SecondaryButton);
        secondaryButtonText = s.children;
        secondaryButtonHandler = s.onClick;
        secondaryButtonColor = s.color;
        secondaryButtonTestId = s.testId;
      }
    }
  }

  const setModal = useSetAtom(pathwayModalAtom);

  // Generate automatic stepKey if one isn't provided
  const effectiveStepKey =
    stepKey ??
    generateContentSignature(
      headerTitle,
      bodyContent,
      primaryButtonText,
      footerContent,
    );

  // No dep array is intentional — render-sync pattern; the set call is cheap.
  // Functional update: when closed, only clear the atom if this instance owns
  // it. This prevents other mounted-but-closed PathwayModal instances from
  // clobbering a different modal that is currently open.
  useEffect(() => {
    const id = modalIdRef.current;
    if (isOpen) {
      setModal({
        _ownerId: id,
        isOpen: true,
        onClose,
        stepKey: effectiveStepKey,
        modalSize: size,
        className,
        overlayClassName,
        overlayStyle,
        showCloseButton,
        headerTitle,
        headerContent,
        bodyContent,
        footerContent,
        primaryButtonText,
        primaryButtonHandler,
        primaryButtonColor,
        primaryButtonVariant,
        primaryButtonTestId,
        isDisabled,
        isLoading,
        secondaryButtonText,
        secondaryButtonHandler,
        secondaryButtonColor,
        secondaryButtonTestId,
      });
    } else {
      setModal(prev => {
        if (prev._ownerId !== id) return prev;
        return { ...prev, isOpen: false, _ownerId: undefined };
      });
    }
  });

  // On unmount, close the atom if this instance still owns it.
  // This handles cases where the parent conditionally unmounts the composer
  // (e.g. `confirmationModalType` set to null by a button handler) without
  // first flipping isOpen to false.
  useEffect(() => {
    const id = modalIdRef.current;
    return () => {
      setModal(prev => {
        if (prev._ownerId !== id) return prev;
        return pathwayModalInitialState;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

/**
 * Composable modal interface. Returns null — place anywhere alongside
 * `<PathwayModalRenderer />` which must be rendered once higher in the tree.
 */

// eslint-disable-next-line react-refresh/only-export-components -- this component is a pure data carrier, not a visual component
export const PathwayModal = Object.assign(PathwayModalComposer, {
  Title,
  Header,
  Body,
  Footer,
  PrimaryButton,
  SecondaryButton,
});

/**
 * Ownership model & race conditions
 * ----------------------------------
 * The atom holds a single `_ownerId` — the `useId()` ref of whichever composer
 * last wrote `isOpen: true`. Every write from a closed composer is guarded by a
 * functional update that bails out if `prev._ownerId !== id`, so a mounted-but-
 * closed sibling can never clobber a different modal that is currently open.
 *
 * Known race windows:
 *
 * 1. Fast close → open (< 300 ms):
 *    `handleOpenChange` schedules `resetModal` 300 ms after the overlay starts
 *    exiting (to let the exit animation finish). If a new composer takes
 *    ownership within that window, the `setTimeout` callback uses a functional
 *    update (`prev.isOpen ? prev : reset`) and skips the reset, so the new
 *    modal is safe.
 *
 * 2. Two composers open simultaneously:
 *    The last render wins — the atom only holds one state snapshot. The dev-
 *    mode console warning fires immediately when a second owner writes
 *    `isOpen: true` while `__DEV_activeModalsRef` already has an entry.
 *
 * 3. Unmount without close (e.g. route change):
 *    If the composer unmounts while `isOpen` is still true, the cleanup effect
 *    resets the atom to `pathwayModalInitialState`. Because `PathwayModalRenderer`
 *    lives above the route boundary in `LayoutWrapper`, it stays mounted and
 *    React Aria's normal `data-exiting` animation still plays. This is the
 *    correct behavior for route changes — the modal cleans itself up without
 *    any manual intervention from the parent. But this doesn't really matter for
 *    development. It's just something to be aware of.
 */

export default PathwayModal;

// ---------------------------------------------------------------------------
// PathwayModalRenderer (singleton) — reads atom, renders the actual modal UI
// ---------------------------------------------------------------------------

type AnimationState = 'idle' | 'fading-out' | 'resizing' | 'fading-in';

export const PathwayModalRenderer: React.FC = () => {
  const atomState = useAtomValue(pathwayModalAtom);
  const resetModal = useSetAtom(pathwayModalAtom);

  const bodyContent = atomState.bodyContent;
  const bodyText = atomState.bodyText;
  const className = atomState.className;
  const footerContent = atomState.footerContent;
  const headerContent = atomState.headerContent;
  const headerTitle = atomState.headerTitle;
  const isDisabled = atomState.isDisabled;
  const isError = atomState.isError;
  const isLoading = atomState.isLoading;
  const isOpen = atomState.isOpen ?? false;
  const modalSize = atomState.modalSize ?? 'md';
  const onClose = atomState.onClose;
  const overlayClassName = atomState.overlayClassName;
  const overlayStyle = atomState.overlayStyle;
  const primaryButtonColor = atomState.primaryButtonColor ?? 'secondary';
  const primaryButtonHandler = atomState.primaryButtonHandler;
  const primaryButtonTestId = atomState.primaryButtonTestId;
  const primaryButtonText = atomState.primaryButtonText ?? '';
  const primaryButtonVariant = atomState.primaryButtonVariant ?? 'filled';
  const secondaryButtonColor = atomState.secondaryButtonColor ?? 'secondary';
  const secondaryButtonHandler = atomState.secondaryButtonHandler;
  const secondaryButtonTestId = atomState.secondaryButtonTestId;
  const secondaryButtonText = atomState.secondaryButtonText;
  const showCloseButton = atomState.showCloseButton ?? true;
  const stepKey = atomState.stepKey;

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [animationState, setAnimationState] = useState<AnimationState>('idle');
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');
  const [displayedContent, setDisplayedContent] = useState({
    bodyContent,
    bodyText,
    headerTitle,
    headerContent,
    modalSize,
    primaryButtonText,
    primaryButtonHandler,
    primaryButtonColor,
    primaryButtonVariant,
    primaryButtonTestId,
    secondaryButtonText,
    secondaryButtonHandler,
    secondaryButtonColor,
    secondaryButtonTestId,
    footerContent,
  });

  const prevStepKeyRef = useRef(stepKey);
  const prevIsOpenRef = useRef(isOpen);
  const pendingContentRef = useRef({
    bodyContent,
    bodyText,
    headerTitle,
    headerContent,
    modalSize,
    primaryButtonText,
    primaryButtonHandler,
    primaryButtonColor,
    primaryButtonVariant,
    primaryButtonTestId,
    secondaryButtonText,
    secondaryButtonHandler,
    secondaryButtonColor,
    secondaryButtonTestId,
    footerContent,
  });

  const runIdRef = useRef(0);
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  useEffect(() => {
    if (!isOpen) {
      // Modal is closing — preserve displayedContent as-is so the content
      // stays visible throughout the 300ms overlay exit animation. Wiping it
      // here causes a janky resize/blank flash before the overlay finishes.
      prevStepKeyRef.current = stepKey;
      prevIsOpenRef.current = false;
      setAnimationState('idle');
      return;
    }

    // Modal just opened (was previously closed): sync content immediately
    // without a step animation so a fresh open never shows stale content.
    if (!prevIsOpenRef.current) {
      prevIsOpenRef.current = true;
      prevStepKeyRef.current = stepKey;
      setDisplayedContent({
        bodyContent,
        bodyText,
        headerTitle,
        headerContent,
        modalSize,
        primaryButtonText,
        primaryButtonHandler,
        primaryButtonColor,
        primaryButtonVariant,
        primaryButtonTestId,
        secondaryButtonText,
        secondaryButtonHandler,
        secondaryButtonColor,
        secondaryButtonTestId,
        footerContent,
      });
      return;
    }

    const hasStepChanged = prevStepKeyRef.current !== stepKey;
    if (!hasStepChanged) return;

    prevStepKeyRef.current = stepKey;
    pendingContentRef.current = {
      bodyContent,
      bodyText,
      headerTitle,
      headerContent,
      modalSize,
      primaryButtonText,
      primaryButtonHandler,
      primaryButtonColor,
      primaryButtonVariant,
      primaryButtonTestId,
      secondaryButtonText,
      secondaryButtonHandler,
      secondaryButtonColor,
      secondaryButtonTestId,
      footerContent,
    };

    setAnimationState(s => (s === 'idle' ? 'fading-out' : s));
  }, [
    stepKey,
    isOpen,
    bodyContent,
    bodyText,
    headerTitle,
    headerContent,
    modalSize,
    primaryButtonText,
    secondaryButtonText,
    footerContent,
    primaryButtonHandler,
    primaryButtonColor,
    primaryButtonVariant,
    primaryButtonTestId,
    secondaryButtonHandler,
    secondaryButtonColor,
    secondaryButtonTestId,
  ]);

  const handleAnimation = async (state: AnimationState) => {
    const runId = ++runIdRef.current;

    const stillLatest = () => runId === runIdRef.current;

    switch (state) {
      case 'fading-out': {
        if (modalBodyRef.current) {
          setContentHeight(modalBodyRef.current.offsetHeight);
        }

        await sleep(STEP_DURATION);
        if (!stillLatest()) return;

        setDisplayedContent({
          bodyContent: pendingContentRef.current.bodyContent,
          bodyText: pendingContentRef.current.bodyText,
          headerTitle: pendingContentRef.current.headerTitle,
          headerContent: pendingContentRef.current.headerContent,
          modalSize: pendingContentRef.current.modalSize,
          primaryButtonText: pendingContentRef.current.primaryButtonText,
          primaryButtonHandler: pendingContentRef.current.primaryButtonHandler,
          primaryButtonColor: pendingContentRef.current.primaryButtonColor,
          primaryButtonVariant: pendingContentRef.current.primaryButtonVariant,
          primaryButtonTestId: pendingContentRef.current.primaryButtonTestId,
          secondaryButtonText: pendingContentRef.current.secondaryButtonText,
          secondaryButtonHandler:
            pendingContentRef.current.secondaryButtonHandler,
          secondaryButtonColor: pendingContentRef.current.secondaryButtonColor,
          secondaryButtonTestId:
            pendingContentRef.current.secondaryButtonTestId,
          footerContent: pendingContentRef.current.footerContent,
        });
        setAnimationState('resizing');
        return;
      }

      case 'resizing': {
        if (modalBodyRef.current) {
          const el = modalBodyRef.current;
          const original = el.style.height;

          el.style.height = 'auto';
          const newHeight = el.offsetHeight;
          el.style.height = original;
          // force reflow so the next height change transitions reliably
          void el.offsetHeight;

          setContentHeight(newHeight);
        }

        await sleep(STEP_DURATION);
        if (!stillLatest()) return;

        setAnimationState('fading-in');
        return;
      }

      case 'fading-in': {
        await sleep(STEP_DURATION);
        if (!stillLatest()) return;

        setAnimationState('idle');

        if (!stillLatest()) return;

        const el = modalBodyRef.current;

        if (el) {
          const prevTransition = el.style.transition;

          // restore transitions next frame
          requestAnimationFrame(() => {
            if (!stillLatest()) return;
            el.style.transition = prevTransition;
          });
        }

        return;
      }
    }
  };

  useLayoutEffect(() => {
    runIdRef.current++;
    handleAnimation(animationState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationState]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose?.();
      // Reset atom after the overlay's 300ms exit animation. Use a functional
      // update so we only reset if no new modal has taken ownership in the
      // meantime — prevents clobbering a modal that opens within 300ms.
      setTimeout(() => {
        resetModal(prev => (prev.isOpen ? prev : pathwayModalInitialState));
      }, 300);
    }
  };

  return (
    <Modal.Overlay
      className={cn('z-2000', overlayClassName)}
      data-testid="PathwayModal"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={showCloseButton}
      style={overlayStyle}
    >
      <Modal.Dialog
        size={
          animationState === 'idle' ? modalSize : displayedContent.modalSize
        }
        className={cn(
          'mx-4! flex! w-full! max-w-[610px]! flex-col! overflow-y-hidden! 2xl:max-w-[840px]! [&>section]:flex! [&>section]:h-full! [&>section]:flex-col!',
          className,
        )}
      >
        <ScrollModalHeader
          scrollRef={modalBodyRef}
          className="px-4! md:px-8!"
          style={{
            opacity:
              animationState === 'idle' || animationState === 'fading-in'
                ? 1
                : 0,
            transition: `opacity ${STEP_DURATION}ms ease-in-out`,
          }}
        >
          <p className="text-variant-h3 text-center">
            {animationState === 'idle'
              ? headerTitle
              : displayedContent.headerTitle}
          </p>
          {animationState === 'idle'
            ? headerContent
            : displayedContent.headerContent}
        </ScrollModalHeader>
        <div
          ref={modalBodyRef}
          style={{
            height: contentHeight === 'auto' ? 'auto' : `${contentHeight}px`,
            opacity:
              animationState === 'idle' || animationState === 'fading-in'
                ? 1
                : 0,
            overflow: contentHeight === 'auto' ? 'visible' : 'hidden',
            transition: `height ${STEP_DURATION}ms ease-out, opacity ${STEP_DURATION}ms ease-in-out`,
          }}
          className="min-h-0! flex-auto! overflow-y-auto!"
        >
          <Modal.Content className="m-0! w-full! max-w-none! justify-start! px-4! md:px-8!">
            {(animationState === 'idle'
              ? bodyContent
              : displayedContent.bodyContent) && (
              <div className="mx-auto">
                {animationState === 'idle'
                  ? bodyContent
                  : displayedContent.bodyContent}
              </div>
            )}
          </Modal.Content>
        </div>
        <ScrollModalFooter
          scrollRef={modalBodyRef}
          style={{
            opacity:
              animationState === 'idle' || animationState === 'fading-in'
                ? 1
                : 0,
            transition: `opacity ${STEP_DURATION}ms ease-in-out`,
          }}
        >
          {(
            animationState === 'idle'
              ? footerContent
              : displayedContent.footerContent
          ) ? (
            animationState === 'idle' ? (
              footerContent
            ) : (
              displayedContent.footerContent
            )
          ) : (
            <div className="md:wrap-reverse flex w-full flex-col-reverse justify-center gap-4 md:w-auto md:flex-row md:flex-wrap">
              {(animationState === 'idle'
                ? secondaryButtonHandler
                : displayedContent.secondaryButtonHandler) &&
                (animationState === 'idle'
                  ? secondaryButtonText
                  : displayedContent.secondaryButtonText) && (
                  <Button
                    color={
                      animationState === 'idle'
                        ? secondaryButtonColor
                        : displayedContent.secondaryButtonColor
                    }
                    data-testid={
                      animationState === 'idle'
                        ? (secondaryButtonTestId ??
                          'PathwayModalSecondaryButton')
                        : (displayedContent.secondaryButtonTestId ??
                          'PathwayModalSecondaryButton')
                    }
                    onClick={
                      animationState === 'idle'
                        ? secondaryButtonHandler
                        : displayedContent.secondaryButtonHandler
                    }
                    variant="outlined"
                  >
                    {animationState === 'idle'
                      ? secondaryButtonText
                      : displayedContent.secondaryButtonText}
                  </Button>
                )}
              {(animationState === 'idle'
                ? primaryButtonText
                : displayedContent.primaryButtonText) && (
                <Button
                  color={
                    animationState === 'idle'
                      ? primaryButtonColor
                      : displayedContent.primaryButtonColor
                  }
                  data-testid={
                    animationState === 'idle'
                      ? (primaryButtonTestId ??
                        'ConfirmationPopUpPrimaryButton')
                      : (displayedContent.primaryButtonTestId ??
                        'ConfirmationPopUpPrimaryButton')
                  }
                  disabled={isDisabled}
                  loading={isLoading}
                  onClick={
                    animationState === 'idle'
                      ? primaryButtonHandler
                      : displayedContent.primaryButtonHandler
                  }
                  variant={
                    isError
                      ? 'destructive'
                      : animationState === 'idle'
                        ? primaryButtonVariant
                        : displayedContent.primaryButtonVariant
                  }
                >
                  {animationState === 'idle'
                    ? primaryButtonText
                    : displayedContent.primaryButtonText}
                </Button>
              )}
            </div>
          )}
        </ScrollModalFooter>
      </Modal.Dialog>
    </Modal.Overlay>
  );
};
